import { useMutation } from '@apollo/client';
import AvatarStatusFavorite from '@modules/talents/components/AvatarStatusFavorite';
import { Divider } from '@components/Divider';
import { FlexRow } from '@components/FlexRow';
import { AvatarWithStatusProps } from '@modules/talents/components/AvatarWithStatus';
import { UPDATE_TALENT_STATUS } from '@gql/talents';
import { getHighestDegree } from '@modules/diplomas/helper';
import { Degree } from '@modules/diplomas/types';
import { Box, Chip, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import styled from '@mui/system/styled';
import { useNavigate } from 'react-router-dom';

// --- Styled Components ---

const CardProfileWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    background: theme.palette.dashboard.main,
    padding: '24px',
    borderRadius: '24px',
    alignItems: 'center',
    gap: '12px',
    position: 'relative',
    overflow: 'hidden',
}));

const ProfileDetails = styled(Stack)({
    flex: 1,
    minWidth: 0,
    gap: '4px',
});

const BadgeContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
});

const StyledChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== 'highlight',
})<{ highlight?: boolean }>(({ theme, highlight }) => ({
    backgroundColor: highlight
        ? (theme.palette.transparentPurple?.main || theme.palette.primary.main)
        : theme.palette.backgroundBar.main,
    border: `1px solid ${highlight
        ? (theme.palette.transparentPurple?.main || theme.palette.primary.main)
        : theme.palette.backgroundBar.main
        }`,
    borderRadius: '16px',
    height: '28px',
    fontSize: '14px',
    fontWeight: 700,
    color: theme.palette.neutralWhite.main,
    padding: '6px 10px',
    '& .MuiChip-label': {
        padding: '0 4px',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 700,
    },
    '& .MuiChip-icon': {
        color: theme.palette.neutralWhite.main,
        fontSize: '12px',
        marginLeft: '0px',
        marginRight: '4px',
    },
}));

const MetaText = styled(Typography)(({ theme }) => ({
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: 700,
    '& i': { fontSize: 14, display: 'flex' },
})) as typeof Typography;

const ActionButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: theme.palette.focusPurple?.main || theme.palette.primary.main,
    color: theme.palette.neutralWhite.main,
    width: '48px',
    height: '48px',
    borderRadius: '48px',
    padding: '8px 16px',
    '&:hover': {
        backgroundColor: theme.palette.hoverPurple?.main || theme.palette.primary.dark,
    },
    '& .fi': {
        fontSize: '20px',
    },
}));

const SmallActionButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.neutralDarkGray?.main || theme.palette.divider}`,
    color: theme.palette.neutralWhite.main,
    width: '40px',
    height: '40px',
    borderRadius: '40px',
    padding: '8px',
    '&:hover': {
        backgroundColor: theme.palette.hoverPurple?.main || theme.palette.action.hover,
    },
    '& .fi': {
        fontSize: '16px',
    },
}));

// --- Types ---

interface CardProfileProps {
    onAssignJobClick: (prev: boolean) => void;
    talent: {
        _id: string;
        firstName: string;
        lastName: string;
        city: string;
        yearsOfExperience: number;
        totalJobApplications: number;
        experiences?: {
            title: string;
            type: string;
            startDate: Date;
            endDate: Date;
            current: boolean;
        }[];
        diplomas?: {
            type: Degree;
            title: string;
            startDate: Date;
            endDate: Date;
        }[];
        profilePicture?: {
            url: string;
        };
        currentStatusUser?: AvatarWithStatusProps['status'];
        preferences?: string[];
        upercutFavorites?: Array<{
            _id: string;
            type: string;
            upercutId?: string;
            createdAt: string;
        }>;
        principalProfession?: string;
        email?: string;
        phoneNumber?: string;
        expectedSalary?: number;
        upercutAverageRating?: number;
        profileCompletionRate?: number;
        social?: {
            linkedin?: string;
        };
    };
}

// --- Helpers ---

const formatSalary = (salary?: number): string => {
    if (!salary) return 'N/A';
    if (salary >= 1000) return `${Math.round(salary / 1000)}k€/an`;
    return `${salary}€/an`;
};

// --- Component ---

const CardProfile = ({ onAssignJobClick, talent }: CardProfileProps) => {
    const navigate = useNavigate();

    const [updateStatusTalent, { loading: updatingStatus }] = useMutation(UPDATE_TALENT_STATUS);

    const handleStatusChange = (newStatus: AvatarWithStatusProps['status']) => {
        const backendStatus = newStatus === 'nothing' ? '' : newStatus;

        updateStatusTalent({
            variables: {
                payload: {
                    talentIds: [talent._id],
                    currentStatusUser: backendStatus,
                },
            },
            update: (cache, { data }) => {
                if (data?.updateStatusUsersUpercut) {
                    const talentId = cache.identify({ __typename: 'Talent', _id: talent._id });
                    cache.modify({
                        id: talentId,
                        fields: {
                            currentStatusUser() {
                                return backendStatus;
                            },
                        },
                    });
                }
            },
            optimisticResponse: {
                updateStatusUsersUpercut: true,
            },
        }).catch((error) => {
            console.error('Failed to update talent status:', error);
        });
    };

    const badges = [
        {
            icon: 'fi fi-br-briefcase',
            label: `${talent.totalJobApplications} candidature${talent.totalJobApplications > 1 ? 's' : ''}`,
            tooltip: 'Nombre de candidatures',
            highlight: false,
        },
        {
            icon: 'fi fi-br-diploma',
            label: getHighestDegree(talent.diplomas?.map(d => d.type)) || 'N/A',
            tooltip: 'Diplome le plus eleve',
            highlight: false,
        },
        {
            icon: 'fi fi-br-user-time',
            label: talent?.preferences?.join(', ') || 'N/A',
            tooltip: 'Contrat souhaite par le talent',
            highlight: false,
        },
        {
            icon: 'fi fi-br-money-bill-wave',
            label: formatSalary(talent.expectedSalary),
            tooltip: 'Salaire annuel souhaite (brut)',
            highlight: true,
        },
        ...(talent.upercutAverageRating && talent.upercutAverageRating > 0
            ? [{
                icon: 'fi fi-br-star',
                label: `${talent.upercutAverageRating.toFixed(1)}`,
                tooltip: 'Note moyenne interne',
                highlight: true,
            }]
            : []),
    ];

    const completionRate = talent.profileCompletionRate ?? 0;
    const fullName = `${talent.firstName || ''} ${talent.lastName || ''}`.trim() || 'Nom non renseigne';

    return (
        <CardProfileWrapper>
            {/* Avatar */}
            <AvatarStatusFavorite
                talentIds={talent._id}
                size='md'
                image={talent.profilePicture?.url || ''}
                currentStatus={talent.currentStatusUser}
                upercutFavorites={talent.upercutFavorites}
                onStatusChange={handleStatusChange}
                loading={updatingStatus}
                disabled={updatingStatus}
            />

            {/* Profile Info */}
            <ProfileDetails>
                <Stack gap={1}>
                    <FlexRow gap={1} alignItems="center">

                        <Typography
                            fontSize="24px"
                            fontWeight={700}
                            color="neutralWhite.main"
                            sx={{
                                textDecoration: 'none',
                                cursor: 'pointer',
                                fontFamily: "'LEMON MILK Pro', sans-serif",
                                lineHeight: 'normal',
                            }}
                            onClick={() => navigate(`/talents/edit/${talent._id}`, { state: { data: talent } })}
                        >
                            {fullName}
                        </Typography>
                        {completionRate > 0 && (
                            <Tooltip title={`Profil complete a ${completionRate}%`} arrow placement="bottom">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 140 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={completionRate}
                                        sx={{
                                            flex: 1,
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: 'rgba(255,255,255,0.15)',
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 3,
                                                backgroundColor: completionRate >= 80
                                                    ? 'successGreen.main'
                                                    : completionRate >= 50
                                                        ? 'warningOrange.main'
                                                        : 'errorRed.main',
                                            },
                                        }}
                                    />
                                    <Typography variant="caption" color="textSecondary" fontWeight={700} sx={{ fontSize: '12px' }}>
                                        {completionRate}%
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )}
                    </FlexRow>
                    <FlexRow sx={{ flexWrap: 'wrap' }}>
                        <MetaText variant="body2" color="textSecondary">
                            <i className="fi fi-br-marker" />
                            {talent.city || 'N/A'}
                        </MetaText>
                        <Divider />
                        <MetaText variant="body2" color="textSecondary">
                            <i className="fi fi-br-briefcase" />
                            {Math.round(talent.yearsOfExperience)} {talent.yearsOfExperience > 1 ? 'ans' : 'an'} d&apos;exp.
                        </MetaText>
                        {talent.email && (
                            <>
                                <Divider />
                                <MetaText
                                    variant="body2"
                                    color="textSecondary"
                                    component="a"
                                    href={`mailto:${talent.email}`}
                                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    <i className="fi fi-br-envelope" />
                                    {talent.email}
                                </MetaText>
                            </>
                        )}
                        {talent.phoneNumber && (
                            <>
                                <Divider />
                                <MetaText
                                    variant="body2"
                                    color="textSecondary"
                                    component="a"
                                    href={`tel:${talent.phoneNumber}`}
                                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    <i className="fi fi-br-phone-flip" />
                                    {talent.phoneNumber}
                                </MetaText>
                            </>
                        )}
                    </FlexRow>
                    {/* Badges */}
                    <FlexRow>
                        {badges.map((badge, index) => (
                            <Tooltip key={index} title={badge.tooltip} arrow placement="top">
                                <StyledChip
                                    highlight={badge.highlight}
                                    icon={<i className={badge.icon} />}
                                    label={badge.label}
                                    size="small"
                                />
                            </Tooltip>
                        ))}
                    </FlexRow>
                </Stack>

                {/* {currentJobTitle && (
                    <Typography
                        variant="body2"
                        color="neutralWhite.main"
                        fontWeight={600}
                        sx={{ opacity: 0.85, fontSize: '15px' }}
                    >
                        {currentJobTitle}
                    </Typography>
                )} */}
            </ProfileDetails>

            {/* Actions */}
            <FlexRow gap={1}>
                <Tooltip title="Proposer une offre" arrow>
                    <ActionButton onClick={() => onAssignJobClick(true)}>
                        <i className="fi fi-br-briefcase" />
                    </ActionButton>
                </Tooltip>
                {talent.social?.linkedin && (
                    <Tooltip title="Voir le profil LinkedIn" arrow>
                        <SmallActionButton
                            {...{ component: 'a', href: talent.social.linkedin, target: '_blank', rel: 'noopener noreferrer' } as any}
                        >
                            <i className="fi fi-brands-linkedin" />
                        </SmallActionButton>
                    </Tooltip>
                )}
            </FlexRow>
        </CardProfileWrapper>
    );
};

export default CardProfile;
